import { borderRadius } from '@eversports/design-tokens/border-radius'
import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '../Box'
import { buttonVariants } from '../Button'

interface Props {
  to: string
  external?: boolean
  blank?: boolean
  ariaLabel?: string
  buttonVariant?: keyof typeof buttonVariants
  dataTestId?: string
}

const Link: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  external = false,
  blank = false,
  to,
  ariaLabel,
  buttonVariant,
  dataTestId,
}) => {
  const blankProps = blank ? { target: `_blank`, rel: 'noopener' } : {}
  const ariaLabelProps = ariaLabel ? { 'aria-label': ariaLabel } : {}

  if (buttonVariant) {
    // Many of our links look like buttons
    // We can't have a link wrap a button - so we can optionally style links to look like buttons
    // Otherwise, it's semantically off, and if you use the keyboard to navigate, it'll double focus (once on the link, once on the button)
    const variantStyles = {
      ...buttonVariants[buttonVariant].sx,
      borderRadius: `${borderRadius.button}px`,
      width: '100%',
      '& > a': {
        paddingX: 4,
        paddingY: 2,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    }
    return external ? (
      <Box sx={{ ...variantStyles }}>
        <a href={to} {...blankProps} {...ariaLabelProps} data-testid={dataTestId}>
          {children}
        </a>
      </Box>
    ) : (
      <Box sx={{ ...variantStyles }}>
        <RouterLink to={to} {...ariaLabelProps} {...blankProps} data-testid={dataTestId}>
          {children}
        </RouterLink>
      </Box>
    )
  }

  return external ? (
    <a href={to} {...blankProps} {...ariaLabelProps} data-testid={dataTestId}>
      {children}
    </a>
  ) : (
    <RouterLink to={to} {...ariaLabelProps} {...blankProps} data-testid={dataTestId}>
      {children}
    </RouterLink>
  )
}

export default Link
